import type {
  AIChatDTO,
  BlockDTO,
  BlockType,
  ConversationDetailDTO,
  QAQueryOptionDTO,
  QAQuestionDTO,
  SearchEngineResultDTO,
} from '@next-space/fe-api-idl';
import { ChatStatusType, QASearchSource } from '@next-space/fe-api-idl';
import type { ReactNode } from 'react';
import { DefaultAIModel } from '../../ai-const';
import { getAIModel } from './utils';

export enum ReferencePageType {
  PAGE = 'page',
  FILE = 'file',
}

export interface CreateChatParams {
  keywords: string;
  parentId?: string;
  spaceId: string;
  qaQueryOption?: QAQueryOptionDTO;
  questionId?: string;
}

// ------------------------------------------------------------

export enum ChatStatus {
  /** 默认状态 */
  DEFAULT = 0,
  /** 初始化，整个都在加载中 */
  LOADING = 1,
  /** 发送消息时，还没开始触发 AI 流 */
  CREATING = 2,
  /** 发送消息时，触发 AI 流 */
  SENDING = 3,
}

export enum ChatUIStyle {
  DEFAULT = 'default',
  LITE = 'lite',
}

export enum SideBarType {
  OUTLINE = 'outline',
  LITE_CHAT = 'lite-chat',
  LITE_CHAT_SKELETON = 'lite-chat-skeleton',
}

export interface SaveArticleParams {
  ai: string;
  user: string;
  saveType: BlockType.PAGE | BlockType.MIND_MAPPING_PAGE;
}

export interface ConversationContextType {
  conversationId: string;
  style: ChatUIStyle;
  spaceId: string;
  parentId?: string;
  sideBarWidth?: number;
  sideBarType?: SideBarType;
  inWiki?: boolean;
  personalWikiId?: string;
  isPersonalWiki?: boolean;
  navigateToChat: (id: string) => void;
  updateContext: (context: Partial<Omit<ConversationContextType, 'updateContext'>>) => void;
  onSaveArticle: (res: SaveArticleParams) => void;
  insertParagraph?: (res: Omit<SaveArticleParams, 'saveType'>) => void;
  renderPageIcon?: (block: BlockDTO) => React.ReactNode;
  openUpgrade?: () => void;
  onSummarize?: () => void;
  openAICoverToShare?: (chatId: string) => void;
  onRenderBlockPreview?: (id: string) => ReactNode;
  onLoadConversation?: (conversation: ConversationDetailDTO) => void;
  navbar?: () => ReactNode;
  updateBlocks?: (blocks?: Record<string, BlockDTO>) => void;
}

export interface RefDataDTO extends SearchEngineResultDTO {
  id?: string;
  type: 'block' | 'web';
}

export interface QuestionContextType {
  conversationId: string;
  questionId: string;
  index: number;
  totalIndex: number;
  curChatIndex: number;
  totalChats: number;
  handlePrevious: () => void;
  handleNext: () => void;
}

export interface ConversationEditStore {
  keywords: string;
  qaQueryOption: QAQueryOptionDTO;
}

export const AI_PAGE_ID = 'ai-page';
export const SIDEBAR_AI_PAGE_ID = 'sidebar-ai-page';

export enum EditorNodeType {
  CODE_BLOCK = 'codeBlock',
  MENTION = 'mention',
  HEADING = 'heading',
}

export enum SearchPageStatus {
  LOADING = 'loading',
  DEFAULT = 'default',
}

export interface MentionItem {
  id: string;
  label: string;
}

export const getDefaultConversation = (): Required<ConversationDetailDTO> => ({
  questions: [],
  question: '',
  id: AI_PAGE_ID,
  createdAt: Date.now(),
  updatedAt: Date.now(),
  spaceId: '',
  parentId: '',
});

export const getDefaultQuestion = (): Required<QAQuestionDTO> => ({
  id: AI_PAGE_ID,
  question: '',
  conversationId: AI_PAGE_ID,
  chatList: [],
  createdAt: Date.now(),
});

export const getDefaultConversationEditStore = (): Required<ConversationEditStore> => ({
  keywords: '',
  qaQueryOption: {
    searchBlocks: [],
    qaSearchSource: QASearchSource.AI_MODEL,
    networkSearch: true,
    model: getAIModel() || DefaultAIModel.value,
    deepSearch: false,
  },
});

export const getDefaultChat = (): Required<AIChatDTO> => ({
  uuid: AI_PAGE_ID,
  ai: '',
  user: '',
  createdBy: '',
  quote: '',
  links: [],
  createdAt: Date.now(),
  status: ChatStatusType.SUCCESS,
  reasoning: '',
  resultData: {
    engineResult: [],
    vectorResult: [],
    suggestions: [],
  },
  searchQueries: [],
  searchResults: [],
  qaQueryOption: getDefaultConversationEditStore().qaQueryOption,
});
//后端返回的chunk类型
export const EventTypes = {
  REASONING: 'reasoning', // LLM 推理过程
  CONTENT: 'content', // LLM 最终内容
  FUNCTION_CALL: 'function_call', // 函数调用通知
  INFO: 'info', // 信息
  ERROR: 'error', // 错误信息
  DONE: 'done', // 完成标记
  RESEARCH_START: 'research_start', // 深度研究开始
  RESEARCH_QUERY_GENERATED: 'research_query_generated', // 生成搜索查询
  RESEARCH_SEARCH_START: 'research_search_start', // 开始搜索
  RESEARCH_SEARCH_COMPLETE: 'research_search_complete', // 搜索完成
  RESEARCH_REFLECTION: 'research_reflection', // 研究反思
  RESEARCH_ROUND_COMPLETE: 'research_round_complete', // 研究轮次完成
  RESEARCH_PROGRESS: 'research_progress', // 研究进度
};
